<template>
  <div class="star-container">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
export default {
  name: 'StarAnimation',
  data() {
    return {
      CONFIG: {
        hue: 217,
        maxStars: 2000,       // 星星数量
        starBaseSize: 60,     // 基础大小
        speedFactor: 500000,   // 速度因子(越大越慢)
        trailAlpha: 0.5,       // 拖尾透明度
        bgColor: `hsla(217, 64%, 6%, 2)` // 背景色
      },
      stars: [],
      canvas: null,
      ctx: null,
      starCache: null,
      animationFrameId: null
    };
  },
  mounted() {
    this.init();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  },
  methods: {
    init() {
      this.canvas = this.$refs.canvas;
      this.ctx = this.canvas.getContext('2d');
      this.starCache = this.createStarCache();
      this.resizeCanvas();
      this.createStars();
      this.animate();
    },
    createStarCache() {
      const cacheCanvas = document.createElement('canvas');
      const cacheCtx = cacheCanvas.getContext('2d');
      const size = 100;
      const half = size / 2;

      cacheCanvas.width = size;
      cacheCanvas.height = size;

      const gradient = cacheCtx.createRadialGradient(
        half, half, 0,
        half, half, half
      );

      gradient.addColorStop(0.025, '#CCC');
      gradient.addColorStop(0.1, `hsl(${this.CONFIG.hue}, 61%, 33%)`);
      gradient.addColorStop(0.25, `hsl(${this.CONFIG.hue}, 64%, 6%)`);
      gradient.addColorStop(1, 'transparent');

      cacheCtx.fillStyle = gradient;
      cacheCtx.beginPath();
      cacheCtx.arc(half, half, half, 0, Math.PI * 2);
      cacheCtx.fill();

      return cacheCanvas;
    },
    resizeCanvas() {
      // 获取实际显示尺寸
      const displayWidth = window.innerWidth;
      const displayHeight = window.innerHeight;

      // 检查是否需要调整
      if (this.canvas.width !== displayWidth || this.canvas.height !== displayHeight) {
        this.canvas.width = displayWidth;
        this.canvas.height = displayHeight;
        return true; // 返回是否发生了尺寸变化
      }
      return false;
    },
    handleResize() {
      if (this.resizeCanvas()) {
        // 如果尺寸变化了，重新创建星星
        this.createStars();
      }
    },
    createStars() {
      this.stars = [];
      for (let i = 0; i < this.CONFIG.maxStars; i++) {
        this.stars.push(this.createStar());
      }
    },
    random(min, max) {
      if (max === undefined) {
        max = min;
        min = 0;
      }
      return min + Math.random() * (max - min);
    },
    maxOrbit(x, y) {
      const max = Math.max(x, y);
      const diameter = Math.sqrt(max * max + max * max);
      return diameter / 2;
    },
    animate() {
      // 绘制拖尾效果
      this.ctx.globalCompositeOperation = 'source-over';
      this.ctx.globalAlpha = this.CONFIG.trailAlpha;
      this.ctx.fillStyle = this.CONFIG.bgColor;
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

      // 绘制星星
      this.ctx.globalCompositeOperation = 'lighter';
      this.stars.forEach(star => star.draw());

      this.animationFrameId = requestAnimationFrame(this.animate);
    },
    // 创建星星对象
    createStar() {
      const self = this;
      return {
        orbitRadius: self.random(self.maxOrbit(self.canvas.width, self.canvas.height)),
        radius: self.random(self.CONFIG.starBaseSize, this.orbitRadius) / 18,
        orbitX: self.canvas.width / 2,
        orbitY: self.canvas.height / 2,
        timePassed: self.random(0, self.CONFIG.maxStars),
        speed: self.random(this.orbitRadius) / self.CONFIG.speedFactor,
        alpha: self.random(2, 10) / 10,
        reset() {
          this.orbitRadius = self.random(self.maxOrbit(self.canvas.width, self.canvas.height));
          this.radius = self.random(self.CONFIG.starBaseSize, this.orbitRadius) / 18;
          this.orbitX = self.canvas.width / 2;
          this.orbitY = self.canvas.height / 2;
          this.timePassed = self.random(0, self.CONFIG.maxStars);
          this.speed = self.random(this.orbitRadius) / self.CONFIG.speedFactor;
          this.alpha = self.random(2, 10) / 10;
        },
        draw() {
          const x = Math.sin(this.timePassed) * this.orbitRadius + this.orbitX;
          const y = Math.cos(this.timePassed) * this.orbitRadius + this.orbitY;

          // 闪烁效果
          const twinkle = Math.floor(self.random(10));
          if (twinkle === 1 && this.alpha > 0) {
            this.alpha -= 0.05;
          } else if (twinkle === 2 && this.alpha < 1) {
            this.alpha += 0.05;
          }

          self.ctx.globalAlpha = this.alpha;
          self.ctx.drawImage(
            self.starCache,
            x - this.radius / 2,
            y - this.radius / 2,
            this.radius,
            this.radius
          );

          this.timePassed += this.speed;

          // 如果星星移出视口，重置位置
          if (x < 0 || x > self.canvas.width || y < 0 || y > self.canvas.height) {
            this.reset();
          }
        }
      };
    }
  }
};
</script>

<style scoped>
.star-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  overflow: hidden;
  background: #000;
}

canvas {
  display: block;
  width: 100%;
  height: 100%;
}
</style>